import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { usePermissions } from "../context/PermissionContext";
import Area56LoaderPage from "../pages/Area56LoaderPage";

interface ProtectedRouteProps {
  requiredRoles?: number[]; // Legacy role check
  requiredPermissions?: number[]; // Permission-based check
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  requiredRoles,
  requiredPermissions,
}) => {
  const { data: user, isLoading: isUserLoading } = useAuth();
  const { hasAnyPermission, isLoading: isPermissionsLoading } =
    usePermissions();

  const token = localStorage.getItem("token");
  const location = useLocation();

  // Show loading state while authentication or permissions are being checked
  if (isUserLoading || isPermissionsLoading) return <Area56LoaderPage />;

  // Base authentication check
  if (!user || !token) {
    return <Navigate to="/login" replace />;
  }

  // Permission-based check (takes priority if specified)
  if (requiredPermissions && requiredPermissions.length > 0) {
    // Check if user has ANY of the required permissions
    const hasRequiredPermission = hasAnyPermission(requiredPermissions);

    if (!hasRequiredPermission) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }
  // Legacy role-based check (only use if no permissions are specified)
  else if (requiredRoles && requiredRoles.length > 0) {
    // Get role ID from user data - handle all possible sources
    const userRole =
      user.roleId ||
      (user.roles && user.roles.id) ||
      (user.roleInfo && user.roleInfo.role && user.roleInfo.role.id);

    if (!userRole || !requiredRoles.includes(userRole)) {
      return <Navigate to="/unauthorized" state={{ from: location }} replace />;
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
